import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import PrintHeader from '../print/PrintHeader';
import { Branding } from '../../hooks/useBranding';

// ── Shared print types ─────────────────────────────────────────────

export interface PrintVitals {
  bp_systolic?:  number | null;
  bp_diastolic?: number | null;
  pulse?:        number | null;
  temp_c?:       number | null;
  spo2?:         number | null;
  weight_kg?:    number | null;
  height_cm?:    number | null;
  rr?:           number | null;
}

export interface PrintHospital {
  name:       string;
  address:    string;
  phone:      string;
  licenseNo?: string;
  tagline?:   string;
  logoUrl?:   string;
  settings?:  Branding;
}

export interface PrintDoctor {
  name:            string;
  qualifications:  string;
  regNo?:          string;
  specialization?: string;
}

export interface PrintPatient {
  name:   string;
  uid:    string;
  opdNo?: string;
  age?:   number | null;
  gender: string;
}

export interface PrescriptionPrintLayoutProps {
  hospital:    PrintHospital;
  doctor:      PrintDoctor;
  patient:     PrintPatient;
  encounterId: string;
  date?:       string;
  vitals?:     PrintVitals;
  children:    React.ReactNode;
  onDone:      () => void;
}

// ── Vitals cell ────────────────────────────────────────────────────

function VitalCell({
  label, value, unit, last = false,
}: {
  label:  string;
  value?: number | string | null;
  unit:   string;
  last?:  boolean;
}) {
  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      gap:           '2px',
      padding:       '4px 6px',
      borderRight:   last ? 'none' : '1px solid #d1d5db',
      flex:          last ? 1 : '0 0 auto',
    }}>
      <span style={{
        fontSize:      '6pt',
        color:         '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
        {value != null && value !== '' ? (
          <span style={{ fontSize: '9pt', fontWeight: 700, color: '#111827' }}>
            {String(value)}
          </span>
        ) : (
          <span style={{
            display:      'inline-block',
            width:        '20mm',
            borderBottom: '1px dotted #9ca3af',
            height:       '9px',
          }}/>
        )}
        {unit && (
          <span style={{ fontSize: '6pt', color: '#9ca3af' }}>{unit}</span>
        )}
      </div>
    </div>
  );
}

// ── Shared layout ──────────────────────────────────────────────────

export default function PrescriptionPrintLayout({
  hospital, doctor, patient, encounterId, date, vitals, children, onDone,
}: PrescriptionPrintLayoutProps) {
  const branding = hospital.settings;

  const printDate = date
    ? format(new Date(date), 'dd MMM yyyy')
    : format(new Date(), 'dd MMM yyyy');

  const skipDoctorBlock   = branding?.doctor_details_in_header === true;
  const customHeaderHtml  = branding?.prescription_custom_header_html ?? '';
  const footerHtml        = branding?.prescription_footer_html ?? '';

  // ── Portal target ─────────────────────────────────────────────
  const portalRef = useRef<HTMLDivElement | null>(null);
  if (!portalRef.current) {
    let el = document.getElementById('as-print-portal') as HTMLDivElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = 'as-print-portal';
      document.body.appendChild(el);
    }
    portalRef.current = el;
  }

  // ── Inject @page CSS and trigger print ───────────────────────
  useEffect(() => {
    const style    = document.createElement('style');
    style.id       = 'as-rx-page';
    style.textContent = `@page { size: A4 portrait; margin: 0mm !important; }`;
    document.head.appendChild(style);

    const frameId = requestAnimationFrame(() => {
      window.print();
      onDone();
    });
    return () => {
      cancelAnimationFrame(frameId);
      style.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Compute BMI from vitals if not already provided ──────────
  const bmiDisplay = (() => {
    const w = vitals?.weight_kg;
    const h = vitals?.height_cm;
    if (w && h && h > 0) {
      const hm = h / 100;
      return (w / (hm * hm)).toFixed(1);
    }
    return null;
  })();

  const content = (
    <div style={{
      width:           '100%',
      height:          '100vh',
      overflow:        'hidden',
      display:         'flex',
      flexDirection:   'column',
      boxSizing:       'border-box',
      padding:         '0 5mm 4mm',
      fontFamily:      'Arial, Helvetica, sans-serif',
      color:           '#111',
      backgroundColor: '#fff',
    }}>

      {/* ── 1. HOSPITAL HEADER ──────────────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        {branding ? (
          <PrintHeader
            type="prescription"
            settings={branding}
            hospitalName={hospital.name}
          />
        ) : (
          <div style={{
            display:       'flex',
            alignItems:    'flex-start',
            gap:           '8px',
            paddingBottom: '5px',
            borderBottom:  '2px solid #1f2937',
            marginBottom:  '4px',
          }}>
            {hospital.logoUrl && (
              <img
                src={hospital.logoUrl}
                alt="logo"
                style={{ width: '14mm', height: '14mm', objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: '12pt', lineHeight: 1.2 }}>{hospital.name}</div>
              {hospital.tagline && <div style={{ fontSize: '7pt', color: '#6b7280' }}>{hospital.tagline}</div>}
              {hospital.phone   && <div style={{ fontSize: '7pt', color: '#374151' }}>{hospital.phone}</div>}
            </div>
          </div>
        )}
      </div>

      {/* ── 2. CUSTOM HEADER HTML ───────────────────────────────── */}
      {customHeaderHtml && (
        <div
          style={{
            flexShrink:    0,
            paddingBottom: '4px',
            borderBottom:  '1px solid #e5e7eb',
            marginBottom:  '3px',
            fontSize:      '8pt',
            lineHeight:    1.4,
            color:         '#374151',
          }}
          dangerouslySetInnerHTML={{ __html: customHeaderHtml }}
        />
      )}

      {/* ── 3. DOCTOR CREDENTIALS (conditional) ─────────────────── */}
      {!skipDoctorBlock && (
        <div style={{
          paddingBottom: '4px',
          borderBottom:  '1.5px solid #1a1a2e',
          marginBottom:  '4px',
          flexShrink:    0,
        }}>
          <div style={{ fontSize: '9.5pt', fontWeight: 700, color: '#1f2937', marginBottom: '1px' }}>
            {doctor.name}
          </div>
          <div style={{ fontSize: '7pt', color: '#374151', lineHeight: 1.4 }}>
            {doctor.qualifications}
            {doctor.specialization ? ` · ${doctor.specialization}` : ''}
          </div>
          {doctor.regNo && (
            <div style={{ fontSize: '7pt', color: '#6b7280', marginTop: '1px' }}>
              Reg. No: {doctor.regNo}
            </div>
          )}
        </div>
      )}

      {/* ── 4. PATIENT DEMOGRAPHICS BAR ──────────────────────────── */}
      <div style={{
        display:       'flex',
        alignItems:    'center',
        flexWrap:      'wrap',
        gap:           '10px',
        padding:       '5px 8px',
        background:    '#f3f4f6',
        border:        '1px solid #e5e7eb',
        borderRadius:  '3px',
        marginBottom:  '4px',
        flexShrink:    0,
        fontSize:      '8pt',
        color:         '#374151',
      }}>
        <span>
          <span style={{ fontSize: '7pt', color: '#6b7280' }}>Patient: </span>
          <strong>{patient.name}</strong>
        </span>
        <span>
          <span style={{ fontSize: '7pt', color: '#6b7280' }}>Age/Sex: </span>
          <strong>
            {patient.age != null ? `${patient.age}y` : ''}
            {' '}{patient.gender === 'male' ? 'M' : patient.gender === 'female' ? 'F' : 'O'}
          </strong>
        </span>
        <span>
          <span style={{ fontSize: '7pt', color: '#6b7280' }}>Date: </span>
          <strong>{printDate}</strong>
        </span>
        <span>
          <span style={{ fontSize: '7pt', color: '#6b7280' }}>ID: </span>
          <strong style={{ fontFamily: 'monospace' }}>{patient.uid}</strong>
        </span>
        {patient.opdNo && (
          <span>
            <span style={{ fontSize: '7pt', color: '#6b7280' }}>OPD: </span>
            <strong style={{ fontFamily: 'monospace' }}>{patient.opdNo}</strong>
          </span>
        )}
      </div>

      {/* ── 5. VITALS GRID ───────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        border:       '1px solid #e5e7eb',
        borderRadius: '3px',
        marginBottom: '5px',
        flexShrink:   0,
        overflow:     'hidden',
      }}>
        {vitals?.bp_systolic && (
          <VitalCell
            label="BP"
            value={`${vitals.bp_systolic}/${vitals.bp_diastolic ?? '?'}`}
            unit="mmHg"
          />
        )}
        {!vitals?.bp_systolic && <VitalCell label="BP" value={null} unit="mmHg"/>}
        <VitalCell label="Pulse"  value={vitals?.pulse}     unit="/min"/>
        <VitalCell label="Temp"   value={vitals?.temp_c}    unit="C"/>
        <VitalCell label="SpO2"   value={vitals?.spo2}      unit="%"/>
        <VitalCell label="Weight" value={vitals?.weight_kg} unit="kg"/>
        <VitalCell label="Height" value={vitals?.height_cm} unit="cm"/>
        <VitalCell label="BMI"    value={bmiDisplay}        unit="" last/>
      </div>

      {/* ── 6. CONTENT SLOT (canvas or clinical data) ────────────── */}
      <div style={{
        flex:          '1 1 0',
        overflow:      'hidden',
        display:       'flex',
        flexDirection: 'column',
      }}>
        {children}
      </div>

      {/* ── 7. FOOTER ─────────────────────────────────────────────── */}
      <div style={{
        borderTop:  '1px solid #9ca3af',
        paddingTop: '4px',
        marginTop:  '3px',
        flexShrink: 0,
        fontSize:   '6.5pt',
        color:      '#6b7280',
      }}>
        {footerHtml ? (
          <div
            style={{ marginBottom: '2px', color: '#4b5563', fontSize: '7pt' }}
            dangerouslySetInnerHTML={{ __html: footerHtml }}
          />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
            <span style={{
              maxWidth:     '75mm',
              overflow:     'hidden',
              textOverflow: 'ellipsis',
              whiteSpace:   'nowrap',
            }}>
              {hospital.address}
            </span>
            <span style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
              {hospital.phone    && <span>{hospital.phone}</span>}
              {hospital.licenseNo && <span>Lic: {hospital.licenseNo}</span>}
            </span>
          </div>
        )}
      </div>

    </div>
  );

  return createPortal(content, portalRef.current);
}
